/**
 * LeadsTableV2 - Enhanced table component with sorting, selection, and custom columns
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Avatar,
  Chip,
  Typography,
  IconButton,
  CircularProgress,
  Button,
} from '@mui/material';
import {
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Phone as PhoneIcon,
  Mail as MailIcon,
  CalendarMonth as CalendarIcon,
  Schedule as ScheduleIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import LeadsActionMenu from '../../../components/common/LeadsActionMenu';

// Export column definitions for use in ColumnCustomizer
export const ALL_COLUMNS = [
  // Basic Information
  { id: 'lead_id', label: 'Lead ID', sortable: true, width: 80, minWidth: 70 },
  { id: 'name', label: 'Name', sortable: true, width: 130, minWidth: 110 },
  { id: 'email', label: 'Email', sortable: true, width: 150, minWidth: 120 },
  { id: 'mobile', label: 'Mobile', sortable: true, width: 120, minWidth: 100 },
  
    // Dates & Timeline
    { id: 'created_at', label: 'Created', sortable: true, width: 110, minWidth: 100 },
    { id: 'first_contact_at', label: 'First Contact', sortable: true, width: 110, minWidth: 100 },
    { id: 'last_contacted_at', label: 'Last Contact', sortable: true, width: 110, minWidth: 100 },
    { id: 'lead_age', label: 'Lead Age', sortable: true, width: 90, minWidth: 80 },
    { id: 'time_since_last_contact', label: 'Days Since Contact', sortable: true, width: 120, minWidth: 100 },
  
  // Status & Stage
  { id: 'stage', label: 'Stage', sortable: true, width: 90, minWidth: 80 },
  { id: 'status', label: 'Status', sortable: true, width: 90, minWidth: 80 },
  { id: 'sales_owner', label: 'Owner', sortable: true, width: 110, minWidth: 100 },
  { id: 'is_reassigned', label: 'Reassigned', sortable: true, width: 90, minWidth: 80 },
  
    // Activities & Engagement
    { id: 'next_followup', label: 'Next Followup', sortable: true, width: 110, minWidth: 100 },
    { id: 'next_visit', label: 'Next Visit', sortable: true, width: 110, minWidth: 100 },
    { id: 'last_completed_visit', label: 'Last Completed Visit', sortable: true, width: 130, minWidth: 120 },
    { id: 'site_visit_count', label: 'Site Visits', sortable: true, width: 90, minWidth: 80 },
  { id: 'last_note', label: 'Last Note', sortable: false, width: 150, minWidth: 120 },
  { id: 'all_notes', label: 'Notes Count', sortable: true, width: 90, minWidth: 80 },
  
  // Call Analytics
  { id: 'total_connected_calls', label: 'Connected Calls', sortable: true, width: 110, minWidth: 90 },
  { id: 'total_talktime', label: 'Talktime', sortable: true, width: 100, minWidth: 90 },
  
  // Additional Details
  { id: 'location', label: 'Location', sortable: false, width: 150, minWidth: 120 },
  { id: 'source', label: 'Source', sortable: false, width: 110, minWidth: 90 },
  { id: 'tags', label: 'Tags', sortable: false, width: 110, minWidth: 90 },
  { id: 'channel_partner', label: 'Channel Partner', sortable: false, width: 130, minWidth: 110 },
  { id: 'budget', label: 'Budget', sortable: false, width: 110, minWidth: 100 },
  { id: 'lead_metadata', label: 'Metadata', sortable: false, width: 100, minWidth: 90 },
  { id: 'not_interested_reason', label: 'Not Interested', sortable: false, width: 130, minWidth: 110 },
];

const LeadsTableV2 = ({
  leads = [],
  isLoading = false,
  sortField,
  sortOrder,
  onSort,
  selectedLeads = [],
  onSelectAll,
  onSelectLead,
  onAction,
  isDeleted = false,
  visibleColumns = [],
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const highlightedLeadRef = useRef(null);
  
  // Get highlighted lead ID from location state or sessionStorage (when navigating back from details)
  const [highlightedLeadId, setHighlightedLeadId] = useState(() => {
    // Check location state first (for direct navigation)
    if (location.state?.highlightedLeadId) {
      return location.state.highlightedLeadId;
    }
    // Check sessionStorage (for browser back button)
    const stored = sessionStorage.getItem('highlightedLeadId');
    if (stored) {
      sessionStorage.removeItem('highlightedLeadId'); // Clear after reading
      return parseInt(stored);
    }
    return null;
  });
  
  // Scroll to highlighted row when component mounts or leads change
  useEffect(() => {
    if (highlightedLeadId && highlightedLeadRef.current && leads.length > 0) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        highlightedLeadRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        // Clear highlight after 3 seconds
        setTimeout(() => {
          setHighlightedLeadId(null);
        }, 3000);
      }, 100);
    }
  }, [highlightedLeadId, leads]);
  
  // Filter visible columns
  const columns = visibleColumns.length > 0
    ? ALL_COLUMNS.filter(col => visibleColumns.includes(col.id))
    : ALL_COLUMNS;
  
  // Check if all visible leads are selected
  const allSelected = leads.length > 0 && leads.every(lead => selectedLeads.includes(lead.id));
  const someSelected = leads.some(lead => selectedLeads.includes(lead.id)) && !allSelected;
  
  // Render sort icon
  const renderSortIcon = (columnId) => {
    if (sortField !== columnId) return null;
    return sortOrder === 'asc' ? (
      <ArrowUpwardIcon sx={{ fontSize: 14, color: 'primary.main' }} />
    ) : (
      <ArrowDownwardIcon sx={{ fontSize: 14, color: 'primary.main' }} />
    );
  };
  
  // Format date - compact format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return dayjs(dateString).format('MMM DD, YY');
  };
  
  // Format date with time - compact format
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return dayjs(dateString).format('MMM DD, YY HH:mm');
  };
  
  // Get initials
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };
  
  // Get stage color
  const getStageColor = (stage) => {
    const stageColors = {
      'new': 'info',
      'contacted': 'warning',
      'qualified': 'success',
      'negotiation': 'primary',
      'closure': 'success',
      'junk': 'error',
    };
    return stageColors[stage] || 'default';
  };
  
  // Render cell content
  const renderCell = (column, lead) => {
    switch (column.id) {
      case 'lead_id':
        return (
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
            #{lead.lead_id || lead.id}
          </Typography>
        );
      
      case 'name':
        const nameText = lead.contact?.name || 'N/A';
        return (
          <Typography
            component={Link}
            to={`/leads/${lead.id}`}
            state={{ 
              returnPath: location.pathname + location.search,
              highlightedLeadId: lead.id 
            }}
            variant="body2"
            onClick={(e) => {
              e.stopPropagation();
              // Store the lead ID in sessionStorage for highlighting on return
              sessionStorage.setItem('highlightedLeadId', lead.id.toString());
              if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                window.open(`/leads/${lead.id}`, '_blank');
              }
            }}
            sx={{
              fontWeight: 600,
              fontSize: '0.8125rem',
              color: 'primary.main',
              textDecoration: 'none',
              cursor: 'pointer',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              '&:hover': {
                textDecoration: 'underline',
                color: 'primary.dark'
              }
            }}
            title={nameText}
          >
            {nameText}
          </Typography>
        );
      
      case 'email':
        const emailText = lead.contact?.email || '';
        const truncatedEmail = emailText.length > 20 ? emailText.substring(0, 20) + '...' : emailText;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {lead.contact?.email ? (
              <>
                <MailIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ fontSize: '0.7rem' }}
                  title={emailText.length > 20 ? emailText : undefined}
                >
                  {truncatedEmail}
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>N/A</Typography>
            )}
          </Box>
        );
      
      case 'mobile':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {lead.contact?.mobile ? (
              <>
                <PhoneIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {lead.contact.mobile}
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>N/A</Typography>
            )}
          </Box>
        );
      
      case 'contact_info':
        const mobileText = lead.contact?.mobile || '';
        const truncatedMobile = mobileText.length > 12 ? mobileText.substring(0, 12) + '...' : mobileText;
        const contactEmailText = lead.contact?.email || '';
        const truncatedContactEmail = contactEmailText.length > 18 ? contactEmailText.substring(0, 18) + '...' : contactEmailText;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {lead.contact?.mobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <PhoneIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ fontSize: '0.7rem' }}
                  title={mobileText.length > 12 ? mobileText : undefined}
                >
                  {truncatedMobile}
                </Typography>
              </Box>
            )}
            {lead.contact?.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <MailIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ fontSize: '0.7rem' }}
                  title={contactEmailText.length > 18 ? contactEmailText : undefined}
                >
                  {truncatedContactEmail}
                </Typography>
              </Box>
            )}
            {!lead.contact?.mobile && !lead.contact?.email && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>N/A</Typography>
            )}
          </Box>
        );
      
      case 'created_at':
        return (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {lead.created_at ? formatDateTime(lead.created_at) : 'N/A'}
          </Typography>
        );
      
      case 'first_contact_at':
        return (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {lead.first_contact_at ? formatDateTime(lead.first_contact_at) : 'Never'}
          </Typography>
        );
      
      case 'last_contacted_at':
        return (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {lead.last_contact_at ? formatDateTime(lead.last_contact_at) : 'Never'}
          </Typography>
        );
      
      case 'lead_age':
        return (
          <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
            {lead.lead_age_days !== null && lead.lead_age_days !== undefined ? `${lead.lead_age_days}d` : '-'}
          </Typography>
        );
      
      case 'time_since_last_contact':
        const daysSinceContact = lead.days_since_last_contact;
        return (
          <Typography 
            variant="caption" 
            color={daysSinceContact > 30 ? 'error' : daysSinceContact > 7 ? 'warning.main' : 'text.secondary'} 
            sx={{ fontSize: '0.75rem', fontWeight: daysSinceContact > 30 ? 600 : 400 }}
          >
            {daysSinceContact !== null && daysSinceContact !== undefined ? `${daysSinceContact}d` : '-'}
          </Typography>
        );
      
      case 'stage':
        return (
          <Chip
            label={lead.stage_verbose || lead.stage || 'N/A'}
            size="small"
            color={getStageColor(lead.stage)}
            sx={{
              fontWeight: 500,
              fontSize: '0.7rem',
              height: '20px',
              '& .MuiChip-label': {
                px: 0.75,
                py: 0
              }
            }}
          />
        );
      
      case 'status':
        return (
          <Chip
            label={lead.status_verbose || lead.status || 'N/A'}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: 500,
              fontSize: '0.7rem',
              height: '20px',
              '& .MuiChip-label': {
                px: 0.75,
                py: 0
              }
            }}
          />
        );
      
      case 'budget':
        const budgetMin = lead.budget?.min || lead.preference?.budget_min;
        const budgetMax = lead.budget?.max || lead.preference?.budget_max;
        
        // Backend already formats budget values (e.g., "₹2Cr", "₹200L")
        // Handle cases where values might be missing or null
        if (!budgetMin && !budgetMax) {
          return (
            <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
              N/A
            </Typography>
          );
        }
        
        if (budgetMin && budgetMax) {
          return (
            <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
              {budgetMin} - {budgetMax}
            </Typography>
          );
        }
        
        // If only one value is present, show that one
        const budgetValue = budgetMin || budgetMax;
        return (
          <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.75rem' }}>
            {budgetValue}
          </Typography>
        );
      
      case 'source':
        const sourceValue = lead.source_verbose || lead.source || 'N/A';
        return (
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.75rem', 
              textTransform: 'capitalize',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block'
            }}
            title={sourceValue}
          >
            {sourceValue}
          </Typography>
        );
      
      case 'tags':
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, alignItems: 'center' }}>
            {lead.tags && lead.tags.length > 0 ? (
              <>
                {lead.tags.slice(0, 2).map((tag, idx) => (
                  <Chip
                    key={idx}
                    label={typeof tag === 'string' ? tag : (tag.name || tag)}
                    size="small"
                    sx={{
                      height: '18px',
                      fontSize: '0.65rem',
                      bgcolor: '#7E57C2',
                      color: 'white',
                      '& .MuiChip-label': {
                        px: 0.5,
                        py: 0
                      }
                    }}
                  />
                ))}
                {lead.tags.length > 2 && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    +{lead.tags.length - 2}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>-</Typography>
            )}
          </Box>
        );
      
      case 'sales_owner':
        const salesOwner = lead.assigned_agent?.name || lead.agent?.name || 'Unassigned';
        const salesOwnerInitials = (lead.assigned_agent?.name || lead.agent?.name) ? getInitials(salesOwner) : '?';
        const truncatedSalesOwner = salesOwner.length > 12 ? salesOwner.substring(0, 12) + '...' : salesOwner;
        const agentId = lead.assigned_agent?.id || lead.agent?.id;
        const isUnassigned = salesOwner === 'Unassigned' || !agentId;
        
        return (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8
              }
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (isUnassigned) {
                // Open assign agent popup for unassigned leads
                onAction('assignAgent', lead);
              } else if (agentId) {
                // Open agent report for assigned leads
                window.open(`/reports/agent/${agentId}`, '_blank');
              }
            }}
          >
            <Avatar
              sx={{
                width: 24,
                height: 24,
                fontSize: '0.65rem',
                bgcolor: isUnassigned 
                  ? (theme) => theme.palette.grey[400]
                  : (theme) => theme.palette.primary.main,
              }}
            >
              {salesOwnerInitials}
            </Avatar>
            <Typography 
              variant="caption" 
              color={isUnassigned ? 'primary' : 'text.secondary'}
              sx={{ 
                fontWeight: isUnassigned ? 600 : 400,
                fontSize: '0.75rem',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'primary.main'
                }
              }}
              title={salesOwner.length > 12 ? salesOwner : undefined}
            >
              {truncatedSalesOwner}
            </Typography>
          </Box>
        );
      
      case 'is_reassigned':
        return (
          <Chip 
            label={lead.is_reassigned ? 'Yes' : 'No'}
            size="small"
            color={lead.is_reassigned ? 'warning' : 'default'}
            sx={{
              height: '20px',
              fontSize: '0.7rem',
              fontWeight: 500,
              '& .MuiChip-label': { px: 0.75, py: 0 }
            }}
          />
        );
      
      case 'next_followup':
        const nextFollowup = lead.next_followup;
        if (nextFollowup) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <ScheduleIcon sx={{ fontSize: 12, color: 'primary.main' }} />
              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                {dayjs(nextFollowup.datetime).format('MMM DD, YY HH:mm')}
              </Typography>
            </Box>
          );
        }
        return <Typography variant="caption" color="text.secondary">-</Typography>;
      
      case 'next_visit':
        const nextVisit = lead.next_visit;
        if (nextVisit) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <CalendarIcon sx={{ fontSize: 12, color: 'success.main' }} />
              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                {dayjs(nextVisit.datetime).format('MMM DD, YY HH:mm')}
              </Typography>
            </Box>
          );
        }
        return <Typography variant="caption" color="text.secondary">-</Typography>;
      
      case 'last_completed_visit':
        const lastCompletedVisit = lead.last_completed_visit;
        if (lastCompletedVisit) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <CalendarIcon sx={{ fontSize: 12, color: 'success.main' }} />
              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                {dayjs(lastCompletedVisit).format('MMM DD, YY HH:mm')}
              </Typography>
            </Box>
          );
        }
        return <Typography variant="caption" color="text.secondary">-</Typography>;
      
      case 'site_visit_count':
        return (
          <Chip 
            label={lead.site_visit_count || 0}
            size="small"
            color={lead.site_visit_count > 0 ? 'success' : 'default'}
            sx={{
              height: '20px',
              fontSize: '0.7rem',
              fontWeight: 500,
              '& .MuiChip-label': { px: 0.75, py: 0 }
            }}
          />
        );
      
      case 'last_note':
        return (
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.7rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '150px',
              display: 'block'
            }}
            title={lead.last_note?.note || ''}
          >
            {lead.last_note?.note ? (lead.last_note.note.length > 50 ? lead.last_note.note.substring(0, 50) + '...' : lead.last_note.note) : '-'}
          </Typography>
        );
      
      case 'all_notes':
        return (
          <Chip 
            label={lead.notes_count || 0}
            size="small"
            color={lead.notes_count > 0 ? 'primary' : 'default'}
            sx={{
              height: '20px',
              fontSize: '0.7rem',
              fontWeight: 500,
              '& .MuiChip-label': { px: 0.75, py: 0 }
            }}
          />
        );
      
      case 'total_connected_calls':
        return (
          <Chip 
            label={lead.total_connected_calls || 0}
            size="small"
            color={lead.total_connected_calls > 0 ? 'info' : 'default'}
            sx={{
              height: '20px',
              fontSize: '0.7rem',
              fontWeight: 500,
              '& .MuiChip-label': { px: 0.75, py: 0 }
            }}
          />
        );
      
      case 'total_talktime':
        return (
          <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
            {lead.total_talktime_formatted || '0m'}
          </Typography>
        );
      
      case 'channel_partner':
        const channelPartnerName = lead.channel_partner?.name || '-';
        return (
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block'
            }}
            title={channelPartnerName}
          >
            {channelPartnerName}
          </Typography>
        );
      
      case 'lead_metadata':
        const metadataCount = lead.metadata ? Object.keys(lead.metadata).length : 0;
        return (
          <Chip 
            label={metadataCount > 0 ? `${metadataCount} items` : 'None'}
            size="small"
            color={metadataCount > 0 ? 'secondary' : 'default'}
            sx={{
              height: '20px',
              fontSize: '0.7rem',
              '& .MuiChip-label': { px: 0.75, py: 0 }
            }}
          />
        );
      
      case 'not_interested_reason':
        const notInterestedReason = lead.lost_reason_verbose || '-';
        return (
          <Typography 
            variant="caption" 
            color={lead.lost_reason_verbose ? 'error' : 'text.secondary'}
            sx={{ 
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block'
            }}
            title={notInterestedReason}
          >
            {notInterestedReason}
          </Typography>
        );
      
      default:
        return <Typography variant="body2">-</Typography>;
    }
  };
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
      <TableContainer 
        sx={{ 
          overflowX: 'auto',
          overflowY: 'visible',
          width: '100%',
          '&::-webkit-scrollbar': {
            height: 6
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: 3,
            backgroundColor: 'rgba(0,0,0,0.2)'
          }
        }}
      >
        <Table size="small" sx={{ width: '100%', tableLayout: 'auto' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: (theme) => theme.palette.grey[50] }}>
              <TableCell padding="checkbox" sx={{ pl: 1, pr: 0.5, width: 48 }}>
                <Checkbox
                  color="primary"
                  size="small"
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  inputProps={{ 'aria-label': 'select all leads' }}
                />
              </TableCell>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    cursor: column.sortable ? 'pointer' : 'default',
                    userSelect: 'none',
                    width: `${(column.width / 1200) * 100}%`,
                    minWidth: column.minWidth,
                    maxWidth: column.width,
                    px: 1,
                    py: 1,
                    fontSize: '0.75rem',
                    '&:hover': column.sortable ? {
                      backgroundColor: (theme) => theme.palette.action.hover
                    } : {}
                  }}
                  onClick={() => column.sortable && onSort(column.id)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.75rem',
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={column.label}
                    >
                      {column.label}
                    </Typography>
                    {column.sortable && (
                      <Box sx={{ flexShrink: 0, ml: 0.25 }}>
                        {renderSortIcon(column.id)}
                      </Box>
                    )}
                  </Box>
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 600, width: 60, px: 1, py: 1, fontSize: '0.75rem' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leads.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 2} sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="body1" color="text.secondary">
                    No leads found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              leads.map((lead) => {
                const isSelected = selectedLeads.includes(lead.id);
                const isHighlighted = highlightedLeadId === lead.id;
                return (
                  <TableRow
                    key={lead.id}
                    ref={isHighlighted ? highlightedLeadRef : null}
                    hover
                    selected={isSelected}
                    sx={{
                      cursor: 'pointer',
                      ...(isHighlighted && {
                        backgroundColor: (theme) => theme.palette.mode === 'dark'
                          ? 'rgba(255, 193, 7, 0.2)'
                          : 'rgba(255, 193, 7, 0.15)',
                        borderLeft: '4px solid',
                        borderLeftColor: (theme) => theme.palette.warning.main,
                        animation: 'highlightPulse 2s ease-in-out',
                        '@keyframes highlightPulse': {
                          '0%': {
                            backgroundColor: (theme) => theme.palette.mode === 'dark'
                              ? 'rgba(255, 193, 7, 0.3)'
                              : 'rgba(255, 193, 7, 0.25)',
                          },
                          '100%': {
                            backgroundColor: (theme) => theme.palette.mode === 'dark'
                              ? 'rgba(255, 193, 7, 0.2)'
                              : 'rgba(255, 193, 7, 0.15)',
                          },
                        },
                      }),
                      '&:hover': {
                        backgroundColor: (theme) => isHighlighted
                          ? (theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.25)' : 'rgba(255, 193, 7, 0.2)')
                          : theme.palette.action.hover,
                      },
                      '&.Mui-selected': {
                        backgroundColor: (theme) => theme.palette.mode === 'dark'
                          ? 'rgba(25, 118, 210, 0.16)'
                          : 'rgba(25, 118, 210, 0.08)',
                        '&:hover': {
                          backgroundColor: (theme) => theme.palette.mode === 'dark'
                            ? 'rgba(25, 118, 210, 0.24)'
                            : 'rgba(25, 118, 210, 0.12)',
                        },
                      },
                    }}
                    onClick={(e) => {
                      if (e.target.type === 'checkbox') return;
                      if (!isDeleted) {
                        // Store the lead ID in sessionStorage for highlighting on return
                        sessionStorage.setItem('highlightedLeadId', lead.id.toString());
                        // Also store in location state
                        navigate(`/leads/${lead.id}`, { 
                          state: { 
                            returnPath: location.pathname + location.search,
                            highlightedLeadId: lead.id 
                          } 
                        });
                      }
                    }}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()} sx={{ pl: 1, pr: 0.5, width: 48 }}>
                      <Checkbox
                        color="primary"
                        size="small"
                        checked={isSelected}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectLead(lead.id);
                        }}
                        inputProps={{ 'aria-labelledby': `lead-${lead.id}` }}
                      />
                    </TableCell>
                    {columns.map((column) => (
                      <TableCell 
                        key={column.id}
                        sx={{ 
                          px: 1, 
                          py: 0.75,
                          fontSize: '0.8125rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {renderCell(column, lead)}
                      </TableCell>
                    ))}
                    <TableCell onClick={(e) => e.stopPropagation()} sx={{ px: 1, py: 0.75, width: 60 }}>
                      {!isDeleted ? (
                        <LeadsActionMenu
                          onAddNote={() => onAction('notes', lead)}
                          onUpdateStatus={() => onAction('status', lead)}
                          onView={() => {
                            // Store the lead ID in sessionStorage for highlighting on return
                            sessionStorage.setItem('highlightedLeadId', lead.id.toString());
                            navigate(`/leads/${lead.id}`, { 
                              state: { 
                                returnPath: location.pathname + location.search,
                                highlightedLeadId: lead.id 
                              } 
                            });
                          }}
                          onDelete={() => onAction('delete', lead)}
                          currentStage={lead.stage}
                        />
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          startIcon={<RestoreIcon sx={{ fontSize: 16 }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            onAction('restore', lead);
                          }}
                          sx={{ 
                            fontSize: '0.75rem', 
                            textTransform: 'none',
                            borderRadius: '4px',
                            py: 0.5,
                            px: 1,
                            minWidth: 'auto'
                          }}
                        >
                          Restore
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default LeadsTableV2;

